import { ModalTypes } from "molecules/constants/modal-types";
import BaseModalHeader from "molecules/modals/headers/base-modal-header";
import Modal from "molecules/modals/modal";
import GroupMigrationRequestDto from "models/interfaces/group-migration-request";
import GroupMigrationRequestRecord from "models/view-models/group-migration-request-record";
import ModalPhaseFindTeam from "./modal-phases/find-teams/modal-phase-find-team";
import ModalPhaseUpdateTeamMembers from "organisms/modals/support-user-team-manage-modal/modal-phases/update-team-members/modal-phase-update-team-members";
import ModalPhaseMigrationProgress from "organisms/modals/support-user-team-manage-modal/modal-phases/migration-progress/modal-phase-migration-progress";
import React, { useState } from "react";
import TeamsAdminService from "utilities/services/teams/teams-admin-service";
import useSupportUserPhases, {
    SupportUserModalPhase,
} from "./use-support-user-phases";
import SupportUserSubscriptionRecord from "models/view-models/support-user-subscription-record";
import { ToastManager } from "utilities/toast/toast-manager";

const CSS_CLASS_NAME = "c-support-user-team-manage-modal";

const SupportUserTeamManagementModal = ({
    handleToggle,
    isOpen,
    subscription,
    onRefresh = async () => {},
}: {
    handleToggle: () => void;
    isOpen: boolean;
    subscription: SupportUserSubscriptionRecord;
    onRefresh?: () => Promise<void>;
}) => {
    const {
        phase,
        onBack,
        onNext,
        reset: resetPhases,
    } = useSupportUserPhases(handleToggle);
    const [selectedTeamId, setSelectedTeamId] = useState<number>();
    const [migrationRequest, setMigrationRequest] =
        useState<GroupMigrationRequestRecord | null>(null);

    const handleCloseDialog = () => {
        resetPhases();
        setSelectedTeamId(undefined);
        setMigrationRequest(null);
        handleToggle();
        onRefresh();
    };

    const onTeamIdChange = (teamId: number | undefined) => {
        setSelectedTeamId(teamId);
    };

    const handleMigration = async (
        groupMigrationDto: Partial<GroupMigrationRequestDto>
    ): Promise<GroupMigrationRequestRecord | undefined> => {
        try {
            const migrationRecord = new GroupMigrationRequestRecord(
                groupMigrationDto
            );

            if (!migrationRecord.isValid()) {
                ToastManager.error(
                    "Invalid migration request. Please make sure Source Team and Target Team are not the same."
                );
                return;
            }

            const migrationResponse =
                await TeamsAdminService.migrate(migrationRecord);

            if (migrationResponse.result?.hasErrors()) {
                // There can be errors that do not block the migration but we still want to show them to the admin
                if (migrationResponse.status === 200) {
                    throw migrationResponse.result.errors;
                }

                throw migrationResponse.result.errors;
            }

            return migrationResponse.resultObject;
        } catch (error) {
            throw error;
        }
    };

    const handleMigrationSuccess = (request: GroupMigrationRequestRecord) => {
        setMigrationRequest(request);
        onNext();
    };

    return (
        <Modal
            cssClassName={CSS_CLASS_NAME}
            type={ModalTypes.Base}
            label={"Migrate a Team"}
            closeDialog={handleCloseDialog}
            isVisible={isOpen}>
            <BaseModalHeader
                heading={"Migrate a Team"}
                closeDialog={handleCloseDialog}
            />
            {phase === SupportUserModalPhase.FindTeam && (
                <ModalPhaseFindTeam
                    selectedTeamId={selectedTeamId}
                    onTeamIdChange={onTeamIdChange}
                    onNext={onNext}
                />
            )}
            {phase === SupportUserModalPhase.UpdateTeamMembers && (
                <ModalPhaseUpdateTeamMembers
                    subscription={subscription}
                    selectedTeamId={selectedTeamId!}
                    onMigrateConfirm={handleMigration}
                    onBack={onBack}
                    onSuccess={handleMigrationSuccess}
                />
            )}
            {phase === SupportUserModalPhase.MigrationProgress &&
                migrationRequest != null && (
                    <ModalPhaseMigrationProgress
                        migrationRequest={migrationRequest}
                        onClose={handleCloseDialog}
                    />
                )}
        </Modal>
    );
};

export default SupportUserTeamManagementModal;
